
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNotificationPreferences, DEFAULT_PREFERENCES } from "@/hooks/useNotificationPreferences";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Info } from "lucide-react";
import { useState } from "react";
import { NotificationSection } from "./notifications/NotificationSection";
import { NotificationPreview } from "./notifications/NotificationPreview";
import { notificationSections } from "./notifications/notificationConfig";

export const NotificationSettings = () => {
  const { preferences, isLoading, error, updatePreferences, isUpdating } = useNotificationPreferences();
  const { toast } = useToast();
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notification Preferences</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-8 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notification Preferences</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-8 flex flex-col items-center text-destructive">
            <Info className="h-10 w-10 mb-2" />
            <p className="text-center">Error loading preferences. Please try again later.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const handlePreferenceChange = async (field: string, checked: boolean) => {
    try {
      await updatePreferences({ [field]: checked });

      // Show success toast for specific channels when enabled
      if (checked) {
        if (field === 'email_notifications') {
          toast({
            title: "Email notifications enabled",
            description: "You will now receive notifications via email."
          });
        } else if (field === 'sms_notifications') {
          toast({
            title: "SMS notifications enabled",
            description: "You will now receive notifications via SMS."
          });
        } else if (field === 'web_push_notifications') {
          toast({
            title: "Push notifications enabled",
            description: "You will now receive notifications in your browser."
          });
        }
      }
    } catch (err) {
      console.error(`Failed to update ${field}:`, err);
      toast({
        title: "Error",
        description: "Failed to update notification preferences",
        variant: "destructive"
      });
    }
  };

  const handleSave = async () => {
    toast({
      title: "Preferences saved",
      description: "Your notification preferences have been updated.",
    });
  };

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle>Notification Preferences</CardTitle>
          <CardDescription>Manage how you receive notifications across the platform</CardDescription>
        </CardHeader>
        
        <CardContent>
          <div className="space-y-6">
            {notificationSections.map(section => (
              <NotificationSection
                key={section.id}
                title={section.title}
                description={section.description}
                items={section.items}
                isExpanded={expandedSection === section.id}
                onToggle={() => toggleSection(section.id)}
                preferences={preferences}
                onPreferenceChange={handlePreferenceChange}
                isUpdating={isUpdating}
              />
            ))}

            <NotificationPreview />
          </div>
        </CardContent>
        
        <CardFooter>
          <div className="w-full space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                className="flex-1 bg-ai-indigo hover:bg-ai-indigo/90" 
                onClick={handleSave} 
                disabled={isUpdating}
              >
                {isUpdating ? "Saving..." : "Save Preferences"}
              </Button>
              
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  updatePreferences(DEFAULT_PREFERENCES);
                  toast({
                    title: "Default settings restored",
                    description: "Your notification preferences have been reset to default values."
                  });
                }}
                disabled={isUpdating}
              >
                Reset to Default
              </Button>
            </div>
            
            <div className="text-sm text-gray-500 flex items-start gap-2">
              <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <p>
                You can change your notification preferences at any time. 
                Some system notifications cannot be disabled as they contain important account information.
              </p>
            </div>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};
